import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { ReceiptUploader } from "@/components/files";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

export function useCollection(coll, params = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/c/${coll}`, { params }); setRows(data); } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [coll, key]);
  const create = async (body) => { const { data } = await api.post(`/c/${coll}`, body); await load(); return data; };
  const update = async (id, body) => { const { data } = await api.put(`/c/${coll}/${id}`, body); await load(); return data; };
  const remove = async (id) => { await api.delete(`/c/${coll}/${id}`); await load(); };
  return { rows, loading, reload: load, create, update, remove };
}

export function FormDialog({ open, onOpenChange, title, fields, initial = {}, onSubmit, submitLabel = "Save", testId = "form-dialog" }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial); }, [open]); // eslint-disable-line
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(form); onOpenChange(false); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid={testId}>
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.name} className={f.full || f.type === "textarea" || f.type === "file" ? "sm:col-span-2" : ""}>
                <Field label={f.label} hint={f.hint}>
                  {f.type === "file" ? (
                    <ReceiptUploader value={form[f.name] || []} onChange={(v) => set(f.name, v)} />
                  ) : f.type === "select" ? (
                    <Select value={form[f.name] ?? ""} onValueChange={(v) => set(f.name, v)}>
                      <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea data-testid={`field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} rows={3} />
                  ) : (
                    <Input data-testid={`field-${f.name}`} name={f.name} type={f.type || "text"} required={f.required} value={form[f.name] ?? ""} placeholder={f.placeholder}
                      onChange={(e) => set(f.name, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />
                  )}
                </Field>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="form-submit-button" className="rounded-full px-5">{busy ? "Saving…" : submitLabel}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
